import styles from "./ManageOrganizations.module.css";
import { getPageFromName } from "../../route_utils";
import api_endpoints from "../../api";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DataGrid from "../../widgets/DataGrid/DataGrid";
import CustomTextField from "../../widgets/CustomTextField/CustomTextField";
import { NavigationButton } from "../../widgets/NavigationButton/NavigationButton";
import AddIcon from "@mui/icons-material/Add";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import { Chip } from "@mui/material";
import CustomSelect from "../../widgets/CustomSelect/CustomSelect";
import { useApiClientQuery } from "../../hooks/useApiClient";

const ManageOrganizations = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const cancelTokenSource = useRef(null);
  const [loadingError, setLoadingError] = useState(false);
  const { t } = useCustomTranslation("Organisations"); // Use namespace for translations
  const [tableFilter, setTableFilter] = useState({
    legal_name: "",
    address: "",
    phone: "",
    owner_name: "",
    is_archived: false,
    federal_tax_number: "",
  });

  const columns = [
    {
      field: "legal_name",
      headerName: t("table_org_column_name"),
      sortable: true,
      numberSort: false,
    },
    {
      field: "address",
      headerName: t("table_org_column_address"),
      sortable: true,
      numberSort: false,
    },
    {
      field: "phone",
      headerName: t("table_org_column_phone"),
      sortable: true,
      numberSort: true,
    },
    {
      field: "owner_name",
      headerName: t("table_org_column_owner"),
      sortable: true,
      numberSort: false,
    },
    {
      field: "status",
      headerName: t("table_org_column_status"),
      sortable: false,
      numberSort: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? t("table_org_archive") : t("table_org_active")}
          color={params.value ? "error" : "success"}
          variant="contained"
        />
      ),
    },
    {
      field: "federal_tax_number",
      headerName: t("table_org_column_tax_number"),
      sortable: true,
      numberSort: false,
    },
  ];

  const filters = {
    legal_name: (
      <CustomTextField
        value={tableFilter.legal_name || ""}
        onChange={(e) => changeTableFilter("legal_name", e.target.value)}
        clearFilter={() => clearTableFilter("legal_name")}
      />
    ),
    address: (
      <CustomTextField
        value={tableFilter.address || ""}
        onChange={(e) => changeTableFilter("address", e.target.value)}
        clearFilter={() => clearTableFilter("address")}
      />
    ),
    phone: (
      <CustomTextField
        value={tableFilter.phone || ""}
        onChange={(e) => changeTableFilter("phone", e.target.value)}
        clearFilter={() => clearTableFilter("phone")}
      />
    ),
    owner_name: (
      <CustomTextField
        value={tableFilter.owner_name || ""}
        onChange={(e) => changeTableFilter("owner_name", e.target.value)}
        clearFilter={() => clearTableFilter("owner_name")}
      />
    ),
    status: (
      <CustomSelect
        value={tableFilter.is_archived}
        onChange={(e) => {
          changeTableFilter("is_archived", e.target.value);
        }}
        options={[
          {
            value: "",
            label: t("table_org_all"),
          },
          {
            value: false,
            label: t("table_org_active"),
          },
          {
            value: true,
            label: t("table_org_archive"),
          },
        ]}
      />
    ),
    federal_tax_number: (
      <CustomTextField
        value={tableFilter.federal_tax_number || ""}
        onChange={(e) =>
          changeTableFilter("federal_tax_number", e.target.value)
        }
        clearFilter={() => clearTableFilter("federal_tax_number")}
      />
    ),
  };

  const fetchOrganizationQuery = useApiClientQuery(
    () =>
      api_endpoints.getOrganizations(
        tableFilter.legal_name,
        tableFilter.address,
        tableFilter.phone,
        tableFilter.owner_name,
        tableFilter.is_archived,
        tableFilter.federal_tax_number
      ),
    {
      queryKey: [
        "fetch_organizations", // Base query key
        tableFilter.legal_name, // Filter criteria in the key
        tableFilter.address,
        tableFilter.phone,
        tableFilter.owner_name,
        tableFilter.is_archived,
        tableFilter.federal_tax_number,
      ],
    }
  );

  const changeTableFilter = (option, value) => {
    setTableFilter((prevFilter) => ({ ...prevFilter, [option]: value }));
  };

  const clearTableFilter = (option) => {
    setTableFilter((prevFilter) => ({ ...prevFilter, [option]: "" }));
  };

  const onOrganizationClicked = (e) => {
    navigate(getPageFromName("Modifier l'organisation").path, {
      state: { organization_id: e.id },
    });
  };

  return (
    <>
      <div className={"flex justify-end"}>
        <NavigationButton
          startIcon={<AddIcon />}
          text={"Organisation"}
          path={getPageFromName("Créer une organisation").path}
        />
      </div>
      <div className={styles.table_container_body}>
        <DataGrid
          columns={columns}
          rows={
            fetchOrganizationQuery.data?.map((organization) => ({
              id: organization._id,
              legal_name: organization.legal_name,
              address: organization.address,
              phone: organization.phone,
              owner_name: `${organization.owner?.first_name || ""} ${
                organization.owner?.last_name || ""
              }`.trim(),
              status: organization.is_archived,
              federal_tax_number: organization.federal_tax_number,
            })) || []
          }
          showDetails={onOrganizationClicked}
          filters={filters}
          loading={
            fetchOrganizationQuery.isFetching ||
            fetchOrganizationQuery.isLoading
          }
        />
      </div>
    </>
  );
};

export default ManageOrganizations;
